<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import RateForm from './RateForm.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmation = ref(false);
const selectedRate = ref({});
const isSubmitting = ref(false);

const records = computed(() => getters['motorReservaRates/getRecords'].value);
const uiFlags = computed(() => getters['motorReservaRates/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('MOTOR_RESERVA.RATES.TABLE.NAME'),
  t('MOTOR_RESERVA.RATES.TABLE.TYPE'),
  t('MOTOR_RESERVA.RATES.TABLE.PERIOD'),
  t('MOTOR_RESERVA.RATES.TABLE.MIN_NIGHTS'),
  t('MOTOR_RESERVA.RATES.TABLE.STATUS'),
  t('MOTOR_RESERVA.RATES.TABLE.ACTIONS'),
]);

const rateTypeLabel = type => {
  const labels = {
    standard: t('MOTOR_RESERVA.RATES.TYPES.STANDARD'),
    promotional: t('MOTOR_RESERVA.RATES.TYPES.PROMOTIONAL'),
    package: t('MOTOR_RESERVA.RATES.TYPES.PACKAGE'),
  };
  return labels[type] || type;
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const createRate = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaRates/create', formData);
    useAlert(t('MOTOR_RESERVA.RATES.ADD.API.SUCCESS_MESSAGE'));
    showAddPopup.value = false;
  } catch (error) {
    useAlert(error?.message || t('MOTOR_RESERVA.RATES.ADD.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const updateRate = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaRates/update', {
      id: selectedRate.value.id,
      ...formData,
    });
    useAlert(t('MOTOR_RESERVA.RATES.EDIT.API.SUCCESS_MESSAGE'));
    showEditPopup.value = false;
  } catch (error) {
    useAlert(error?.message || t('MOTOR_RESERVA.RATES.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const deleteRate = async () => {
  try {
    await store.dispatch('motorReservaRates/delete', selectedRate.value.id);
    useAlert(t('MOTOR_RESERVA.RATES.DELETE.API.SUCCESS_MESSAGE'));
    showDeleteConfirmation.value = false;
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.RATES.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const openEdit = rate => {
  selectedRate.value = rate;
  showEditPopup.value = true;
};

const openDelete = rate => {
  selectedRate.value = rate;
  showDeleteConfirmation.value = true;
};

const duplicateRate = async rate => {
  isSubmitting.value = true;
  try {
    const data = {
      motor_reserva_rate: {
        name: `${rate.name} (Cópia)`,
        active: false,
        rate_type: rate.rate_type || 'standard',
        promo_code_only: rate.promo_code_only || false,
        show_in_calendar: rate.show_in_calendar !== false,
        pension_type: rate.pension_type || 'room_only',
        min_stay: rate.min_stay || 1,
        max_stay: rate.max_stay || 0,
        advance_days: rate.advance_days || 0,
        lastminute_days: rate.lastminute_days || 0,
        rich_description: rate.rich_description || '',
        guarantee_type: rate.guarantee_type || 'percentage',
        guarantee_percentage: rate.guarantee_percentage || 0,
        guarantee_description: rate.guarantee_description || '',
        non_refundable: rate.non_refundable || false,
        cancellation_description: rate.cancellation_description || '',
        cancellation_penalties: rate.cancellation_penalties || [],
        payment_methods: rate.payment_methods || [],
        included_extras: rate.included_extras || [],
      },
    };
    await store.dispatch('motorReservaRates/create', data);
    useAlert(t('MOTOR_RESERVA.RATES.DUPLICATE.SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('MOTOR_RESERVA.RATES.DUPLICATE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

onBeforeMount(() => {
  store.dispatch('motorReservaRates/get');
  store.dispatch('motorReservaExtras/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.RATES.HEADER') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-300">
          {{ t('MOTOR_RESERVA.RATES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="t('MOTOR_RESERVA.COMMON.ADD_RATE')"
        @click="showAddPopup = true"
      />
    </div>

    <BaseTable
      :headers="tableHeaders"
      :items="records"
      :loading="uiFlags.isFetching"
    >
      <template #row="{ items }">
        <BaseTableRow v-for="rate in items" :key="rate.id">
          <BaseTableCell>{{ rate.name }}</BaseTableCell>
          <BaseTableCell>
            <span
              class="px-2 py-0.5 text-xs rounded-full"
              :class="{
                'bg-blue-100 text-blue-700': rate.rate_type === 'standard',
                'bg-green-100 text-green-700': rate.rate_type === 'promotional',
                'bg-purple-100 text-purple-700': rate.rate_type === 'package',
              }"
            >
              {{ rateTypeLabel(rate.rate_type) }}
            </span>
          </BaseTableCell>
          <BaseTableCell>
            {{ formatDate(rate.valid_from) }} -
            {{ formatDate(rate.valid_until) }}
          </BaseTableCell>
          <BaseTableCell>{{ rate.min_nights || '-' }}</BaseTableCell>
          <BaseTableCell>
            <span
              class="px-2 py-0.5 text-xs rounded-full"
              :class="
                rate.active
                  ? 'bg-green-100 text-green-700'
                  : 'bg-slate-100 text-slate-500'
              "
            >
              {{
                rate.active
                  ? t('MOTOR_RESERVA.COMMON.ACTIVE_F')
                  : t('MOTOR_RESERVA.COMMON.INACTIVE_F')
              }}
            </span>
          </BaseTableCell>
          <BaseTableCell>
            <div class="flex gap-2">
              <button
                class="text-sm text-woot-500 hover:text-woot-700"
                @click="openEdit(rate)"
              >
                {{ t('MOTOR_RESERVA.COMMON.EDIT') }}
              </button>
              <button
                class="text-sm text-slate-500 hover:text-slate-700"
                :disabled="isSubmitting"
                @click="duplicateRate(rate)"
              >
                {{ t('MOTOR_RESERVA.RATES.DUPLICATE.BUTTON') }}
              </button>
              <button
                class="text-sm text-red-500 hover:text-red-700"
                @click="openDelete(rate)"
              >
                {{ t('MOTOR_RESERVA.COMMON.DELETE') }}
              </button>
            </div>
          </BaseTableCell>
        </BaseTableRow>
      </template>
    </BaseTable>

    <!-- Add Modal -->
    <div
      v-if="showAddPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <h3 class="text-lg font-medium mb-4 text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.RATES.ADD.TITLE') }}
        </h3>
        <RateForm
          :is-submitting="isSubmitting"
          @submit="createRate"
          @cancel="showAddPopup = false"
        />
      </div>
    </div>

    <!-- Edit Modal -->
    <div
      v-if="showEditPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <h3 class="text-lg font-medium mb-4 text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.RATES.EDIT.TITLE') }}
        </h3>
        <RateForm
          :rate="selectedRate"
          :is-submitting="isSubmitting"
          @submit="updateRate"
          @cancel="showEditPopup = false"
        />
      </div>
    </div>

    <!-- Delete Confirmation -->
    <div
      v-if="showDeleteConfirmation"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-sm">
        <p class="text-sm text-slate-700 dark:text-slate-200 mb-4">
          {{ t('MOTOR_RESERVA.RATES.DELETE.CONFIRM') }}
        </p>
        <div class="flex gap-2 justify-end">
          <Button
            variant="secondary"
            :label="t('MOTOR_RESERVA.COMMON.CANCEL')"
            @click="showDeleteConfirmation = false"
          />
          <Button
            variant="danger"
            :label="t('MOTOR_RESERVA.COMMON.DELETE')"
            @click="deleteRate"
          />
        </div>
      </div>
    </div>
  </div>
</template>
